import React, {useMemo, useRef} from "react"
import {School, SchoolClass} from "models"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import AppHistory from "shared/history"
import Card from "components/utils/card"
import {ClassStepsSelectWithContainer} from "components/class-steps/select"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import {Form} from "@kaspernj/api-maker/build/form"
import memo from "set-state-compare/build/memo"
import Routes from "shared/routes"
import {SchoolsSelectWithContainer} from "components/schools/select"
import TeachersSelect from "components/user/admin/school-classes/edit/teachers-select"
import useCollection from "@kaspernj/api-maker/build/use-collection"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import {useFrontLayout} from "components/front-layout"
import useI18n from "i18n-on-steroids/src/use-i18n.mjs"
import useModel from "@kaspernj/api-maker/build/use-model"
import {useParams} from "@kaspernj/api-maker/build/router/route"
import UtilsButton from "components/utils/button"
import UtilsInput from "components/utils/input"
import {v4 as uuidv4} from "uuid"
import {View} from "react-native"

export default memo(shapeComponent(class RoutesUserAdminSchoolClassesEdit extends ShapeComponent {
  setup() {
    const currentUser = useCurrentUser()
    const params = useParams()
    const {t} = useI18n({namespace: "js.routes.user.admin.school_classes.edit"})
    const {schoolClass} = useModel(SchoolClass, {
      match: {params},
      newIfNoId: {
        defaults: () => ({new_id: uuidv4()})
      }
    })
    const {schools} = useCollection({
      modelClass: School,
      ransack: {
        s: "current_translation_name asc",
        contact_children_relationships_child_id_eq: currentUser?.contactId() || "no-user-uuid",
        contact_children_relationships_contact_relationship_type_identifier_eq: "school_leader"
      },
      select: {
        School: ["contactId", "id", "name"]
      }
    })
    const schoolsCacheKey = schools?.map((school) => school.id())?.join("-") || "no-schools"
    const schoolsOptions = useMemo(
      () => schools?.map((school) => ({school, value: school.id(), text: school.name()})),
      [schoolsCacheKey]
    )
    const defaultSchool = useMemo(() => {
      if (schools?.length === 1) {
        return schools[0]
      }
    }, [schoolsCacheKey])

    this.formRef = useRef()
    this.setInstance({defaultSchool, schoolClass, schoolsOptions, t})
    this.useStates({
      classStep: null,
      createNewTeacher: null,
      school: null,
      teacherId: null
    })

    useMemo(() => {
      this.setState({
        classStep: schoolClass?.classStepId(),
        school: this.s.school || defaultSchool
      })
    }, [schoolClass?.id(), schoolsCacheKey])

    useFrontLayout().setState({active: "user-admin-school-classes", contentCentered: true})
  }

  render() {
    const {defaultSchool, schoolClass, schoolsOptions, t} = this.tt

    return (
      <View dataSet={this.rootViewDataSet ||= {route: "user--admin--school-classes--edit"}}>
        {schoolClass && schoolsOptions &&
          <Card style={{maxWidth: 490}}>
            <Form formObjectRef={this.formRef}>
              <UtilsInput
                dataSet={{class: "class-name-input"}}
                defaultValue={schoolClass?.name()}
                label={t(".class_name")}
                name="school_class[name]"
                style={{marginBottom: 10}}
              />
              {schoolsOptions &&
                <SchoolsSelectWithContainer
                  defaultValue={defaultSchool?.id()}
                  id="school_id"
                  label={School.modelName().human()}
                  onChange={this.tt.onSchoolChanged}
                  options={schoolsOptions}
                />
              }
              <ClassStepsSelectWithContainer
                defaultValue={this.s.classStep}
                id="class_step_id"
                label={t(".class_step")}
                onChange={this.tt.onChangeClassStep}
              />
              {this.s.school &&
                <TeachersSelect
                  id="teacher_id"
                  label={t(".attached_teachers")}
                  onChange={this.tt.onTeacherChanged}
                  school={this.s.school}
                />
              }
              {this.s.createNewTeacher &&
                <>
                  <UtilsInput
                    label={t(".first_name_of_teacher")}
                    name="teacher[first_name]"
                    style={{marginBottom: 10}}
                    testID="teacher-first-name-input"
                  />
                  <UtilsInput
                    label={t(".last_name_of_teacher")}
                    name="teacher[last_name]"
                    style={{marginBottom: 10}}
                    testID="teacher-last-name-input"
                  />
                  <UtilsInput
                    label={t(".email_of_teacher")}
                    name="teacher[email]"
                    style={{marginBottom: 10}}
                    testID="teacher-email-input"
                  />
                </>
              }
              <UtilsButton
                onPress={this.tt.onSaveClassPressed}
                style={this.saveButtonStyle ||= {marginTop: 10}}
                testID="save-school-class-button"
                title={schoolClass.isNewRecord() ? t(".create_class") : t(".update_class")}
              />
            </Form>
          </Card>
        }
      </View>
    )
  }

  onChangeClassStep = ({options}) => this.setState({classStep: options[0]?.value})
  onSchoolChanged = ({options}) => this.setState({school: options[0]?.school})

  onTeacherChanged = ({options}) => {
    const option = options[0]

    if (option?.optionType == "teacher") {
      this.setState({
        createNewTeacher: false,
        teacherId: option.value
      })
    } else if (option?.optionType == "create-new-teacher") {
      this.setState({
        createNewTeacher: true,
        teacherId: null
      })
    }
  }

  onSaveClassPressed = async () => {
    const {t} = this.tt
    const form = this.formRef.current
    const schoolClassName = form?.getValue("school_class[name]")
    const teacherFirstName = form?.getValue("teacher[first_name]")
    const teacherLastName = form?.getValue("teacher[last_name]")
    const teacherEmail = form?.getValue("teacher[email]")

    try {
      const isNewSchoolClass = this.tt.schoolClass.isNewRecord()
      const saveData = {
        school_class: {
          name: schoolClassName,
          class_step_id: this.s.classStep,
          school_id: this.s.school.id()
        }
      }

      if (this.s.teacherId) {
        saveData.school_class_role = {
          role: "teacher",
          user_id: this.s.teacherId
        }
      } else if (this.s.createNewTeacher) {
        const password = uuidv4()

        saveData.school_class_role = {
          role: "teacher",
          user_attributes: {
            email: teacherEmail,
            first_name: teacherFirstName,
            last_name: teacherLastName,
            password,
            password_confirmation: password
          }
        }
      } else {
        FlashMessage.alert(t(".you_must_choose_an_existing_user_or_create_a_new_one"))

        return
      }

      await SchoolClass.schoolLeaderCreate(saveData)
      FlashMessage.success(isNewSchoolClass ? this.t(".the_school_class_was_created") : t(".the_school_class_was_updated"))
      AppHistory.push(Routes.userAdminSchoolClassesPath())
    } catch (e) {
      FlashMessage.errorResponse(e)
    }
  }
}))
